/**
 * ThreeTopfSimulationEngine.ts
 *
 * 3-Töpfe Strategy Implementation
 *
 * Implements the efficient portfolio withdrawal strategy as described in the official documentation.
 *
 * **Strategy Overview:**
 * For retirement portfolio management with optimal risk/return during withdrawal phase.
 * Example: 1M Euro capital, 60k Euro annual consumption needs.
 *
 * **Three Pots Allocation:**
 * - **Verbrauchstopf**: 1 year consumption (60k) in money market (0% volatility)
 * - **Zinstopf**: 3 years consumption (180k) in short-term bonds (low volatility)
 * - **Investitionstopf**: Remainder (760k) in stocks/equity (high volatility, higher returns)
 *
 * **Withdrawal Strategy:**
 * - **Good Markets**: Refill Verbrauchstopf from Investitionstopf, preserve Zinstopf
 * - **Crisis Markets (1-3 years)**: Use Zinstopf to refill Verbrauchstopf, protect Investitionstopf
 * - **Post-Crisis**: Refill BOTH Verbrauchstopf and Zinstopf from recovered Investitionstopf
 * - **Extended Crisis (4+ years)**: Last resort use of Investitionstopf when Zinstopf depleted
 *
 * **Key Benefits:**
 * - Avoids selling stocks at unfavorable prices during typical 1-3 year market crises
 * - Maintains equity market participation throughout retirement for long-term growth
 * - Provides `anzahlJahreTopf2` + 1 years of crisis protection before requiring equity
 *   liquidation at poor prices (default: 3 + 1 = 4). The "+1" comes from the one-year
 *   onset lag in crisis detection: strategy decisions for a year are based on the
 *   PREVIOUS year's market status (see getStartOfYearCrisisDecision in
 *   BaseSimulationEngine), so the Zinstopf is not yet protected during the first year
 *   a crisis actually starts.
 * - Balances capital preservation with practical liquidity requirements
 */

import { SteuerParameter } from '../types';
import { BaseSimulationEngine } from './BaseSimulationEngine';
import {
  CrisisDefinitionType,
  PortfolioStrategy,
  RateCalculationContext,
  ThreeTopfInitialValues,
  ThreeTopfSimulationOptions,
  ThreeTopfYearData,
} from '../types/simulation';

export class ThreeTopfSimulationEngine extends BaseSimulationEngine<
  ThreeTopfSimulationOptions,
  ThreeTopfYearData,
  ThreeTopfInitialValues
> {
  constructor(
    steuerParameter: SteuerParameter, 
    options: ThreeTopfSimulationOptions,
    crisisDefinition: CrisisDefinitionType = 'negative-return'
  ) {
    super(steuerParameter, options, crisisDefinition);
  }

  public getStrategy(): PortfolioStrategy {
    return 'three-topf';
  }

  public calculateInitialValues(): ThreeTopfInitialValues {
    const commonValues = this.calculateCommonInitialValues();

    return {
      verbrauchstopf: commonValues.verbrauchstopf,
      zinstopf: commonValues.zinstopf,
      investitionstopf: commonValues.investitionstopf,
      total: commonValues.verbrauchstopf + commonValues.zinstopf + commonValues.investitionstopf,
    };
  }

  public createYearData(year: number, context: RateCalculationContext): ThreeTopfYearData {
    const inflationRate = this.getInflationRate(year);
    const verbrauchstopfRate = this.getVerbrauchstopfRate(year);
    const zinstopfRate = this.getZinstopfRate(year);
    const investitionstopfRate = this.getInvestitionstopfRate(year);

    // Calculate capital index
    const previousKapitalIndex = year === 0 ? 100 : this.kapitalIndexHistory[year - 1];
    const currentKapitalIndex = this.calculateCurrentKapitalIndex(
      year,
      investitionstopfRate,
      previousKapitalIndex
    );

    // Enhanced crisis detection
    const marktSituation = this.getMarktSituation(investitionstopfRate, year, previousKapitalIndex);

    return {
      jahr: context.age, // Use age (retirement age + year) instead of year index
      alter: context.age,
      verbrauchstopfZinssatz: verbrauchstopfRate,
      zinstopfZinssatz: zinstopfRate,
      investitionstopfRendite: investitionstopfRate,
      inflationsrate: inflationRate,
      verbrauchstopfSaldo: 0,
      zinstopfSaldo: 0,
      investitionstopfSaldo: 0,
      gesamtentnahme: 0,
      zielentnahme: 0, // Set in processYearLogic (inflation-adjusted target)
      gesamtsaldo: 0,
      istKrise: marktSituation === 'Krise',
      marktSituation: marktSituation,
      kapitalIndex: currentKapitalIndex,
      crisisState: this.crisisDetectionService.getCrisisState(),
      auffuellAktion: '',
      topf1AuffuellQuelle: '',
      topf2AuffuellAktion: '',
      // Additional properties expected by tests - will be populated in processYearLogic
      entnahme: 0,
      steuern: 0,
      nettoentnahme: 0,
      gesamtkapital: 0,
      rendite: investitionstopfRate,
      // Aliases for pot balances
      verbrauchstopf: 0,
      zinstopf: 0,
      investitionstopf: 0,
      // Tax-related properties
      steueraufRendite: 0,
      steueraufZinsen: 0,
      // Pot-specific withdrawals (optional)
      entnahmeVerbrauchstopf: undefined,
      entnahmeZinstopf: undefined,
      entnahmeInvestitionstopf: undefined,
      nettoentnahmeVerbrauchstopf: undefined,
    };
  }

  protected setInitialBalances(
    yearData: ThreeTopfYearData,
    initialValues: ThreeTopfInitialValues
  ): void {
    yearData.verbrauchstopfSaldo = initialValues.verbrauchstopf;
    yearData.zinstopfSaldo = initialValues.zinstopf;
    yearData.investitionstopfSaldo = initialValues.investitionstopf;
    yearData.gesamtsaldo = initialValues.total;

    // Set additional properties for initial year
    yearData.verbrauchstopf = initialValues.verbrauchstopf;
    yearData.zinstopf = initialValues.zinstopf;
    yearData.investitionstopf = initialValues.investitionstopf;
    yearData.gesamtkapital = initialValues.total;
    yearData.entnahme = 0; // Will be calculated in processYearLogic
    yearData.steuern = 0; // Will be calculated in processYearLogic
    yearData.nettoentnahme = 0; // Will be calculated in processYearLogic
    yearData.steueraufRendite = 0; // Will be calculated in processYearLogic
    yearData.steueraufZinsen = 0; // Will be calculated in processYearLogic
    
    // Store initial balances for TransactionService (before any processing)
    (yearData as any).initialVerbrauchstopfSaldo = initialValues.verbrauchstopf;
    (yearData as any).initialZinstopfSaldo = initialValues.zinstopf;
    (yearData as any).initialInvestitionstopfSaldo = initialValues.investitionstopf;
  }

  /**
   * Calculate Verbrauchstopf interest on declining balance throughout the year
   * Money is withdrawn monthly, so interest is earned on the declining balance
   * 
   * CORRECTED v2.4.4: Fixed calculation order - withdraw FIRST, then apply interest
   * This ensures interest is earned only on the remaining balance after withdrawal
   */
  private calculateVerbrauchstopfInterestOnDecliningBalance(
    balanceAfterRefill: number,
    targetWithdrawal: number,
    annualInterestRate: number
  ): number {
    // For Verbrauchstopf: We withdraw the target amount, but some original balance remains
    // The withdrawal is spread evenly over 12 months
    const monthlyWithdrawal = targetWithdrawal / 12;
    const monthlyInterestRate = annualInterestRate / 100 / 12;
    
    let totalInterest = 0;
    let currentBalance = balanceAfterRefill;
    
    // Calculate interest month by month on the declining balance
    // CORRECTED: Withdraw at START of month, then earn interest on remaining balance
    for (let month = 1; month <= 12; month++) {
      // Withdraw money at the START of the month (before interest accrues)
      currentBalance -= monthlyWithdrawal;
      
      // Don't let balance go below zero
      if (currentBalance < 0) {
        currentBalance = 0;
      }
      
      // Interest earned on the remaining balance AFTER withdrawal
      const interestThisMonth = currentBalance * monthlyInterestRate;
      totalInterest += interestThisMonth;
      
      // Add interest to balance for next month
      currentBalance += interestThisMonth;
    }
    
    return totalInterest;
  }

  public processYearLogic(yearData: ThreeTopfYearData, previousYear?: ThreeTopfYearData): void {
    // Check if this is the first year (no previous year)
    const isFirstYear = !previousYear;
    
    // Copy previous balances or use initial balances
    let verbrauchstopfSaldo: number;
    let zinstopfSaldo: number;
    let investitionstopfSaldo: number;

    // Store starting balances for interest calculation
    const startingVerbrauchstopfSaldo = !previousYear ? yearData.verbrauchstopfSaldo : previousYear.verbrauchstopfSaldo;
    const startingZinstopfSaldo = !previousYear ? yearData.zinstopfSaldo : previousYear.zinstopfSaldo;
    const startingInvestitionstopfSaldo = !previousYear ? yearData.investitionstopfSaldo : previousYear.investitionstopfSaldo;


    // Store original balances BEFORE any processing for TransactionService display
    (yearData as any).originalVerbrauchstopfSaldo = startingVerbrauchstopfSaldo;
    (yearData as any).originalZinstopfSaldo = startingZinstopfSaldo;
    (yearData as any).originalInvestitionstopfSaldo = startingInvestitionstopfSaldo;

    if (!previousYear) {
      // For initial year, use the current balances set by setInitialBalances
      verbrauchstopfSaldo = yearData.verbrauchstopfSaldo;
      zinstopfSaldo = yearData.zinstopfSaldo;
      investitionstopfSaldo = yearData.investitionstopfSaldo;
    } else {
      // Copy previous balances
      verbrauchstopfSaldo = previousYear.verbrauchstopfSaldo;
      zinstopfSaldo = previousYear.zinstopfSaldo;
      investitionstopfSaldo = previousYear.investitionstopfSaldo;
    }

    // Store initial balance for later interest calculation
    const initialVerbrauchstopfBalance = verbrauchstopfSaldo;
    
    // Interest will be calculated and applied later in the process
    // REMOVED: Double interest calculation bug - applyInvestmentReturn was called here AND later

    // Calculate inflation-adjusted withdrawal target based on Portfolio-Entnahme
    const baseWithdrawal =
      (this.steuerParameter.vorhandenesKapital * this.steuerParameter.portfolioEntnahmeRate) / 100;
    // Use simulation year index (age - retirement age), not the actual age
    const simulationYear = yearData.alter - this.steuerParameter.retirementAge;
    
    const inflationAdjustedTarget = this.adjustForInflation(baseWithdrawal, simulationYear);

    // CORRECTED: Market situation for strategy decisions should use start-of-year status
    // Strategy decisions must be made based on information available at the start of the year
    const startOfYearCrisisStatus = this.getStartOfYearCrisisDecision(
      simulationYear, 
      previousYear?.marktSituation
    );
    const isGoodMarket = startOfYearCrisisStatus === 'Hoch';

    // Target calculations for refill/overflow logic
    const zinstopfZielgroesse = inflationAdjustedTarget * this.steuerParameter.anzahlJahreTopf2;
    
    // Calculate deficit based on STARTING balance (before any transactions or interest)
    const zinstopfDefizitBeforeInterest = Math.max(0, zinstopfZielgroesse - startingZinstopfSaldo);
    
    // Start with initial balances - NO INTEREST YET
    // Interest will be applied at the very end of the year
    zinstopfSaldo = startingZinstopfSaldo;
    investitionstopfSaldo = startingInvestitionstopfSaldo;

    // 3-Topf Strategy Logic
    let gesamtentnahme = 0;
    let topf1AuffuellQuelle = '';
    let topf2AuffuellAktion = '';
    let strategyDescription = '';

    // Note: We now calculate net contributions from balance changes at the end
    // instead of tracking individual operations to avoid double-counting

    // Smart refill strategy - Calculate deficit BEFORE any withdrawals
    
    // Calculate how much we need to refill Verbrauchstopf to reach target (BEFORE withdrawal)
    let verbrauchstopfRefillAmount = 0;
    let investitionstopfContributionToVerbrauchstopf = 0;
    // FIX 9: Track the actual Zinstopf amount used for the Verbrauchstopf refill
    // directly (amount-based), instead of re-deriving it later via string-matching
    // on topf1AuffuellQuelle. The previous string-matching approach mis-classified
    // the "Investitionstopf (partiell, Zinstopf leer)" branch (substring "Zinstopf"
    // matched, but the more specific "Investitionstopf (Zinstopf leer)" check did not,
    // due to the extra "partiell, " text), causing a phantom Zinstopf outflow to be
    // recorded even though the Zinstopf was empty and untouched.
    let zinstopfContributionToVerbrauchstopf = 0;
    
    // Überschuss-Skim: Der Zinstopf wird in guten Jahren auf seinem inflationsindexierten
    // Ziel gehalten (zinstopfZielgroesse = inflationAdjustedTarget * anzahlJahreTopf2, s.o.).
    // Liegt der Saldo darüber - z.B. weil der Zinstopf-Zinssatz in diesem Jahr die
    // Inflation übertroffen hat, oder weil ein Vorjahres-Refill mehr als nötig transferiert
    // hat -, wird dieser ÜBERSCHUSS ÜBER dem Ziel zur Deckung der Verbrauchstopf-Entnahme
    // herangezogen, BEVOR Aktien aus dem Investitionstopf verkauft werden (siehe
    // "ueberschussUsed" unten). Das reduziert Aktienverkäufe in Jahren mit hoher
    // Zinstopf-Rendite, ohne den Zinstopf unter sein Ziel absinken zu lassen.
    // BUG FIX v2.4.7: Optimize Investitionstopf withdrawal by using Zinstopf Überschuss first
    // Calculate available Überschuss from Zinstopf (only in good markets, not in first year)
    let availableZinstopfUeberschuss = 0;
    if (!isFirstYear && isGoodMarket && zinstopfSaldo > zinstopfZielgroesse) {
      availableZinstopfUeberschuss = zinstopfSaldo - zinstopfZielgroesse;
    }
    
    if (!isFirstYear && verbrauchstopfSaldo < inflationAdjustedTarget) {
      const verbrauchstopfDefizit = inflationAdjustedTarget - verbrauchstopfSaldo;
      
      if (isGoodMarket) {
        // OPTIMIZATION: First use Zinstopf Überschuss if available, then take from Investitionstopf
        const ueberschussUsed = Math.min(availableZinstopfUeberschuss, verbrauchstopfDefizit);
        const remainingDefizitAfterUeberschuss = verbrauchstopfDefizit - ueberschussUsed;
        
        // Apply Überschuss transfer first
        if (ueberschussUsed > 0) {
          zinstopfSaldo -= ueberschussUsed;
          verbrauchstopfSaldo += ueberschussUsed;
          verbrauchstopfRefillAmount += ueberschussUsed;
          zinstopfContributionToVerbrauchstopf += ueberschussUsed;
        }
        
        // GOOD MARKET: Refill remaining deficit from Investitionstopf, preserve Zinstopf
        if (remainingDefizitAfterUeberschuss > 0 && investitionstopfSaldo >= remainingDefizitAfterUeberschuss) {
          investitionstopfSaldo -= remainingDefizitAfterUeberschuss;
          verbrauchstopfSaldo += remainingDefizitAfterUeberschuss;
          verbrauchstopfRefillAmount += remainingDefizitAfterUeberschuss;
          investitionstopfContributionToVerbrauchstopf = remainingDefizitAfterUeberschuss;
          
          // Build source description
          if (ueberschussUsed > 0) {
            topf1AuffuellQuelle = 'Überschuss Zinstopf + Investitionstopf';
            strategyDescription = `Auffüllung ${Math.round(ueberschussUsed).toLocaleString('de-DE')} € aus Zinstopf-Überschuss + ${Math.round(remainingDefizitAfterUeberschuss).toLocaleString('de-DE')} € aus Investitionstopf`;
          } else {
            topf1AuffuellQuelle = 'Investitionstopf';
            strategyDescription = `Auffüllung ${Math.round(remainingDefizitAfterUeberschuss).toLocaleString('de-DE')} € aus Investitionstopf`;
          }
        } else if (remainingDefizitAfterUeberschuss > 0 && investitionstopfSaldo > 0) {
          // Partial fill from Investitionstopf, then use Zinstopf if needed
          const partialFill = investitionstopfSaldo;
          verbrauchstopfSaldo += partialFill;
          verbrauchstopfRefillAmount += partialFill;
          investitionstopfContributionToVerbrauchstopf = partialFill;
          investitionstopfSaldo = 0;

          const stillRemainingDefizit = remainingDefizitAfterUeberschuss - partialFill;
          if (stillRemainingDefizit > 0 && zinstopfSaldo >= stillRemainingDefizit) {
            zinstopfSaldo -= stillRemainingDefizit;
            verbrauchstopfSaldo += stillRemainingDefizit;
            verbrauchstopfRefillAmount += stillRemainingDefizit;
            zinstopfContributionToVerbrauchstopf += stillRemainingDefizit;

            if (ueberschussUsed > 0) {
              topf1AuffuellQuelle = 'Überschuss Zinstopf + Investitionstopf + Zinstopf';
              strategyDescription = `Auffüllung ${Math.round(ueberschussUsed).toLocaleString('de-DE')} € aus Überschuss + ${Math.round(partialFill).toLocaleString('de-DE')} € aus Investitionstopf + ${Math.round(stillRemainingDefizit).toLocaleString('de-DE')} € aus Zinstopf`;
            } else {
              topf1AuffuellQuelle = 'Investitionstopf + Zinstopf';
              strategyDescription = `Auffüllung ${Math.round(partialFill).toLocaleString('de-DE')} € aus Investitionstopf + ${Math.round(stillRemainingDefizit).toLocaleString('de-DE')} € aus Zinstopf`;
            }
          } else {
            if (ueberschussUsed > 0) {
              topf1AuffuellQuelle = 'Überschuss Zinstopf + Investitionstopf (partiell)';
              strategyDescription = `Auffüllung ${Math.round(ueberschussUsed).toLocaleString('de-DE')} € aus Überschuss + ${Math.round(partialFill).toLocaleString('de-DE')} € aus Investitionstopf - unvollständig`;
            } else {
              topf1AuffuellQuelle = 'Investitionstopf (partiell)';
              strategyDescription = `Auffüllung ${Math.round(partialFill).toLocaleString('de-DE')} € aus Investitionstopf - unvollständig`;
            }
          }
        } else if (ueberschussUsed > 0 && remainingDefizitAfterUeberschuss === 0) {
          // Überschuss fully covered the deficit - no Investitionstopf needed!
          topf1AuffuellQuelle = 'Überschuss Zinstopf';
          strategyDescription = `Auffüllung ${Math.round(ueberschussUsed).toLocaleString('de-DE')} € aus Zinstopf-Überschuss (kein Investitionstopf nötig)`;
        } else {
          // FALLBACK: Investitionstopf is empty, use remaining Zinstopf even in good markets
          const remainingZinstopfDefizit = verbrauchstopfDefizit - ueberschussUsed;
          if (zinstopfSaldo >= remainingZinstopfDefizit) {
            zinstopfSaldo -= remainingZinstopfDefizit;
            verbrauchstopfSaldo += remainingZinstopfDefizit;
            verbrauchstopfRefillAmount += remainingZinstopfDefizit;
            zinstopfContributionToVerbrauchstopf += remainingZinstopfDefizit;

            if (ueberschussUsed > 0) {
              topf1AuffuellQuelle = 'Überschuss Zinstopf + Zinstopf (Investitionstopf erschöpft)';
              strategyDescription = `Auffüllung ${Math.round(ueberschussUsed).toLocaleString('de-DE')} € aus Überschuss + ${Math.round(remainingZinstopfDefizit).toLocaleString('de-DE')} € aus Zinstopf (Investitionstopf erschöpft)`;
            } else {
              topf1AuffuellQuelle = 'Zinstopf (Investitionstopf erschöpft)';
              strategyDescription = `Investitionstopf erschöpft: Auffüllung ${Math.round(remainingZinstopfDefizit).toLocaleString('de-DE')} € aus Zinstopf`;
            }
          } else if (zinstopfSaldo > 0) {
            // Partial fill from Zinstopf
            verbrauchstopfSaldo += zinstopfSaldo;
            verbrauchstopfRefillAmount += zinstopfSaldo;
            zinstopfContributionToVerbrauchstopf += zinstopfSaldo;
            topf1AuffuellQuelle = 'Zinstopf (partiell, Investitionstopf erschöpft)';
            strategyDescription = `Alle Töpfe erschöpft: Auffüllung ${Math.round(zinstopfSaldo).toLocaleString('de-DE')} € aus letztem Zinstopf`;
            zinstopfSaldo = 0;
          }
        }
      } else {
        // CRISIS: Conservative approach - prefer Zinstopf, use Investitionstopf only as LAST RESORT
        if (zinstopfSaldo >= verbrauchstopfDefizit) {
          // Zinstopf can cover the full deficit
          zinstopfSaldo -= verbrauchstopfDefizit;
          verbrauchstopfSaldo += verbrauchstopfDefizit;
          verbrauchstopfRefillAmount = verbrauchstopfDefizit;
          zinstopfContributionToVerbrauchstopf = verbrauchstopfDefizit;
          topf1AuffuellQuelle = 'Zinstopf';
          strategyDescription = `Auffüllung ${Math.round(verbrauchstopfDefizit).toLocaleString('de-DE')} € aus Zinstopf`;
        } else if (zinstopfSaldo > 0) {
          // Use what's available in Zinstopf first, then Investitionstopf if needed
          const partialFillFromZinstopf = zinstopfSaldo;
          verbrauchstopfSaldo += partialFillFromZinstopf;
          verbrauchstopfRefillAmount += partialFillFromZinstopf;
          zinstopfContributionToVerbrauchstopf += partialFillFromZinstopf;
          zinstopfSaldo = 0;

          const remainingDefizit = verbrauchstopfDefizit - partialFillFromZinstopf;
          if (remainingDefizit > 0 && investitionstopfSaldo >= remainingDefizit) {
            // Last resort: use Investitionstopf for remaining need
            investitionstopfSaldo -= remainingDefizit;
            verbrauchstopfSaldo += remainingDefizit;
            verbrauchstopfRefillAmount += remainingDefizit;
            investitionstopfContributionToVerbrauchstopf = remainingDefizit;
            topf1AuffuellQuelle = 'Zinstopf + Investitionstopf (Notfall)';
            strategyDescription = `Auffüllung ${Math.round(partialFillFromZinstopf).toLocaleString('de-DE')} € aus Zinstopf + ${Math.round(remainingDefizit).toLocaleString('de-DE')} € aus Investitionstopf`;
          } else if (investitionstopfSaldo > 0) {
            // Use remaining Investitionstopf (partial)
            verbrauchstopfSaldo += investitionstopfSaldo;
            verbrauchstopfRefillAmount += investitionstopfSaldo;
            investitionstopfContributionToVerbrauchstopf += investitionstopfSaldo;
            topf1AuffuellQuelle = 'Zinstopf + Investitionstopf (partiell)';
            strategyDescription = `Auffüllung ${Math.round(partialFillFromZinstopf).toLocaleString('de-DE')} € aus Zinstopf + ${Math.round(investitionstopfSaldo).toLocaleString('de-DE')} € aus Investitionstopf - Portfolio fast erschöpft`;
            investitionstopfSaldo = 0;
          } else {
            topf1AuffuellQuelle = 'Zinstopf (partiell)';
            strategyDescription = `Auffüllung ${Math.round(partialFillFromZinstopf).toLocaleString('de-DE')} € aus Zinstopf - Investitionstopf erschöpft`;
          }
        } else {
          // CRITICAL: Both Zinstopf and Verbrauchstopf insufficient, must use Investitionstopf
          if (investitionstopfSaldo >= verbrauchstopfDefizit) {
            investitionstopfSaldo -= verbrauchstopfDefizit;
            verbrauchstopfSaldo += verbrauchstopfDefizit;
            verbrauchstopfRefillAmount = verbrauchstopfDefizit;
            investitionstopfContributionToVerbrauchstopf = verbrauchstopfDefizit;
            topf1AuffuellQuelle = 'Investitionstopf (Zinstopf leer)';
            strategyDescription = `Zinstopf leer, Auffüllung ${Math.round(verbrauchstopfDefizit).toLocaleString('de-DE')} € aus Investitionstopf`;
          } else if (investitionstopfSaldo > 0) {
            verbrauchstopfSaldo += investitionstopfSaldo;
            verbrauchstopfRefillAmount = investitionstopfSaldo;
            investitionstopfContributionToVerbrauchstopf = investitionstopfSaldo;
            topf1AuffuellQuelle = 'Investitionstopf (partiell, Zinstopf leer)';
            strategyDescription = `Zinstopf leer, Auffüllung ${Math.round(investitionstopfSaldo).toLocaleString('de-DE')} € aus Investitionstopf - Portfolio fast erschöpft`;
            investitionstopfSaldo = 0;
          } else {
            topf1AuffuellQuelle = 'Keine Auffüllung (Portfolio erschöpft)';
            strategyDescription = `Portfolio vollständig erschöpft`;
          }
        }
      }
    }

    // Primary withdrawal from Verbrauchstopf (now after refill)
    const verbrauchstopfWithdrawal = Math.min(verbrauchstopfSaldo, inflationAdjustedTarget);
    verbrauchstopfSaldo -= verbrauchstopfWithdrawal;
    gesamtentnahme += verbrauchstopfWithdrawal;
    
    // Track the actual withdrawal from Verbrauchstopf balance (for transaction display)
    const actualVerbrauchstopfWithdrawal = verbrauchstopfWithdrawal;
    
    // Store the actual Verbrauchstopf withdrawal for transaction display
    yearData.entnahmeVerbrauchstopf = actualVerbrauchstopfWithdrawal;

    const remainingNeed = inflationAdjustedTarget - gesamtentnahme;

    // Track DIRECT user withdrawals from Zinstopf/Investitionstopf (remainingNeed phase)
    // These are separate from internal refill transfers and are needed so that
    // display/export balance reconstructions match the engine balances.
    let direktentnahmeZinstopf = 0;
    let direktentnahmeInvestitionstopf = 0;

    if (remainingNeed > 0) {
      if (isGoodMarket) {
        // GOOD MARKET STRATEGY: Take directly from Investitionstopf, preserve Zinstopf
        const investitionstopfWithdrawal = Math.min(investitionstopfSaldo, remainingNeed);
        investitionstopfSaldo -= investitionstopfWithdrawal;
        gesamtentnahme += investitionstopfWithdrawal;
        direktentnahmeInvestitionstopf += investitionstopfWithdrawal;

        if (investitionstopfWithdrawal > 0) {
          strategyDescription = `Auffüllung aus Investitionstopf`;
        }

        // FALLBACK: If Investitionstopf couldn't cover all remaining need, use Zinstopf
        const stillRemainingNeed = inflationAdjustedTarget - gesamtentnahme;
        if (stillRemainingNeed > 0) {
          const zinstopfWithdrawal = Math.min(zinstopfSaldo, stillRemainingNeed);
          zinstopfSaldo -= zinstopfWithdrawal;
          gesamtentnahme += zinstopfWithdrawal;
          direktentnahmeZinstopf += zinstopfWithdrawal;

          if (zinstopfWithdrawal > 0) {
            if (investitionstopfWithdrawal > 0) {
              strategyDescription = `Investitionstopf erschöpft, Zinstopf als Fallback`;
            } else {
              strategyDescription = `Investitionstopf erschöpft: Entnahme aus Zinstopf`;
            }
          }
        }
      } else {
        // CRISIS STRATEGY: Conservative approach - use Zinstopf first
        const zinstopfWithdrawal = Math.min(zinstopfSaldo, remainingNeed);
        zinstopfSaldo -= zinstopfWithdrawal;
        gesamtentnahme += zinstopfWithdrawal;
        direktentnahmeZinstopf += zinstopfWithdrawal;

        if (zinstopfWithdrawal > 0) {
          // Don't set final description yet - we need to see if Zinstopf is sufficient
          // This will be refined later in the refill logic
        }

        // If still more needed in crisis, withdraw from Investitionstopf
        const finalRemainingNeed = inflationAdjustedTarget - gesamtentnahme;
        if (finalRemainingNeed > 0) {
          const investitionstopfWithdrawal = Math.min(investitionstopfSaldo, finalRemainingNeed);
          investitionstopfSaldo -= investitionstopfWithdrawal;
          gesamtentnahme += investitionstopfWithdrawal;
          direktentnahmeInvestitionstopf += investitionstopfWithdrawal;

          if (investitionstopfWithdrawal > 0) {
            strategyDescription = `Zinstopf + Investitionstopf (Zinstopf nicht ausreichend)`;
          }
        }
      }
    }

    // Note: Refill logic moved earlier

    // Intelligente Zinstopf-Auffüllung mit korrigierter Berechnung
    // FIXED: Use pre-interest deficit calculation for proper year-long coverage
    let zinstopfCashFlow = 0;

    if (!isFirstYear && zinstopfDefizitBeforeInterest > 0 && isGoodMarket) {
      // Intelligente Auffüllung: Basiert auf Startsaldo (vor Zinsen) für korrekte Jahresabdeckung
      if (investitionstopfSaldo >= zinstopfDefizitBeforeInterest) {
        investitionstopfSaldo -= zinstopfDefizitBeforeInterest;
        zinstopfSaldo += zinstopfDefizitBeforeInterest;
        zinstopfCashFlow = zinstopfDefizitBeforeInterest; // Positive = inflow (from Investitionstopf)
        topf2AuffuellAktion = `Auffüllung: ${Math.round(zinstopfDefizitBeforeInterest).toLocaleString('de-DE')} € aus Investitionstopf`;
      } else if (investitionstopfSaldo > 0) {
        // Partielle Auffüllung mit korrigierter Berechnung
        const maxTransfer = Math.min(investitionstopfSaldo, zinstopfDefizitBeforeInterest);
        zinstopfCashFlow = maxTransfer; // Positive = inflow (from Investitionstopf)
        zinstopfSaldo += maxTransfer;
        investitionstopfSaldo -= maxTransfer;
        topf2AuffuellAktion = `Auffüllung: ${Math.round(maxTransfer).toLocaleString('de-DE')} € aus Investitionstopf (partiell)`;
      }
    } else if (!isFirstYear && zinstopfDefizitBeforeInterest > 0 && !isGoodMarket) {
      // During crisis, skip Zinstopf refill to protect Investitionstopf (NOT in first year)
      topf2AuffuellAktion = `Keine Auffüllung (Krise - Investitionstopf geschützt)`;
    } else if (!isFirstYear && zinstopfDefizitBeforeInterest <= 0 && isGoodMarket) {
      // Zinstopf bereits optimal gefüllt - keine weitere Auffüllung nötig
      topf2AuffuellAktion = `${Math.round(zinstopfSaldo).toLocaleString('de-DE')} €`;
    } else if (isFirstYear) {
      // First year: No Zinstopf refills
      topf2AuffuellAktion = `Startjahr: Keine Auffüllung`;
    }

    // FIX 9: Account for Zinstopf outflows to Verbrauchstopf using the actual tracked
    // contribution amount (zinstopfContributionToVerbrauchstopf), not string-matching
    // on topf1AuffuellQuelle. The previous substring-based classification mis-handled
    // the "Investitionstopf (partiell, Zinstopf leer)" branch: the "partiell, " text
    // inserted between "Investitionstopf (" and "Zinstopf leer)" defeated the specific
    // includes('Investitionstopf (Zinstopf leer)') check, so it fell through to the
    // generic else-branch and wrongly recorded a Zinstopf outflow equal to the full
    // refill amount even though the Zinstopf was empty and the refill came entirely
    // from the Investitionstopf. zinstopfCashFlow keeps any positive inflow set above
    // (Investitionstopf -> Zinstopf refill) and nets out the real Zinstopf -> Verbrauchstopf
    // outflow tracked directly at each transfer site.
    zinstopfCashFlow -= zinstopfContributionToVerbrauchstopf;

    // No tax calculations - using gross withdrawal amounts
    const steueraufRendite = 0;
    const steueraufZinsen = 0;
    const steuern = 0;
    const nettoentnahme = gesamtentnahme;

    // NOTE: A former "CRITICAL FIX" block subtracted the remaining withdrawal deficit
    // from the Verbrauchstopf here. That was mathematically wrong: whenever a deficit
    // remained, the Verbrauchstopf balance was provably already 0 (the withdrawal was
    // min(balance, target)), so the block only created negative balances without
    // increasing gesamtentnahme. A deficit that cannot be withdrawn IS depletion -
    // the block has been removed to keep the conservation equation intact.

    // Zinstopf-Optimierung: Obergrenzenkontrolle (BEFORE interest is applied)
    // Der Zinstopf soll nicht über die strategische Zielgröße hinauswachsen
    // ABER NUR in guten Märkten - in Krisen soll er seine volle Größe behalten
    // WICHTIG: Erst ab dem zweiten Jahr - im ersten Jahr erfolgt nur die initiale Aufteilung
    // Dies ist die ZWEITE Gelegenheit für einen Überschuss-Skim (siehe Kommentar bei
    // availableZinstopfUeberschuss oben): Übersteigt der Zinstopf sein inflationsindexiertes
    // Ziel weiterhin, nachdem der oben berechnete Überschuss ggf. schon für die
    // Verbrauchstopf-Auffüllung verwendet wurde, fließt der VERBLEIBENDE Überschuss direkt
    // in den Verbrauchstopf (statt im Zinstopf über das Ziel hinaus zu verbleiben). Auch das
    // reduziert Aktienverkäufe aus dem Investitionstopf im Folgejahr.
    // BUG FIX v2.4.7: Don't transfer Überschuss again if we already used it for refill
    let zinstopfUeberschussTransfer = 0;
    
    if (!isFirstYear && isGoodMarket && zinstopfSaldo > zinstopfZielgroesse) {
      // Only transfer if we haven't already used this Überschuss for Verbrauchstopf refill
      const remainingUeberschuss = zinstopfSaldo - zinstopfZielgroesse;
      
      // Check if this is the same Überschuss we already used (compare with availableZinstopfUeberschuss)
      // If availableZinstopfUeberschuss was used for refill, don't transfer it again
      const alreadyUsedForRefill = availableZinstopfUeberschuss > 0;
      
      if (!alreadyUsedForRefill && remainingUeberschuss > 0) {
        zinstopfUeberschussTransfer = remainingUeberschuss;
        verbrauchstopfSaldo += zinstopfUeberschussTransfer;
        zinstopfSaldo = zinstopfZielgroesse;
      }
    }
    
    // NOW apply interest to all pots at the very end (correct transaction order)
    const zinstopfInterestEarned = zinstopfSaldo * (yearData.zinstopfZinssatz / 100);
    const investitionstopfInterestEarned = investitionstopfSaldo * (yearData.investitionstopfRendite / 100);
    
    zinstopfSaldo += zinstopfInterestEarned;
    investitionstopfSaldo += investitionstopfInterestEarned;
    
    // Logging für Überschuss-Transfer (falls aufgetreten)
    if (zinstopfUeberschussTransfer > 0) {
      const existingAction = topf2AuffuellAktion;
      topf2AuffuellAktion = `${existingAction}, Überschuss an Verbrauchstopf: ${Math.round(zinstopfUeberschussTransfer).toLocaleString('de-DE')} €`;
    }
    
    // Calculate and apply Verbrauchstopf interest at the end of the year
    // Interest is earned on the declining balance throughout the year as money is withdrawn monthly
    // The balance after refill should include both refills AND overflow transfers from Zinstopf
    const balanceAfterRefill = initialVerbrauchstopfBalance + (verbrauchstopfRefillAmount || 0) + zinstopfUeberschussTransfer;
    const verbrauchstopfInterestEarned = this.calculateVerbrauchstopfInterestOnDecliningBalance(
      balanceAfterRefill,
      inflationAdjustedTarget,
      yearData.verbrauchstopfZinssatz
    );
    verbrauchstopfSaldo += verbrauchstopfInterestEarned;
    
    // BUG FIX v2.4.8: Ensure balances never go negative
    // Cap each pot at zero to prevent negative portfolio values
    verbrauchstopfSaldo = Math.max(0, verbrauchstopfSaldo);
    zinstopfSaldo = Math.max(0, zinstopfSaldo);
    investitionstopfSaldo = Math.max(0, investitionstopfSaldo);
    
    // Update year data
    yearData.verbrauchstopfSaldo = verbrauchstopfSaldo;
    yearData.zinstopfSaldo = zinstopfSaldo;
    yearData.investitionstopfSaldo = investitionstopfSaldo;
    yearData.gesamtentnahme = gesamtentnahme;
    // Store the inflation-adjusted target withdrawal so the success criterion can compare
    // the actually served withdrawal against the target in the target year.
    yearData.zielentnahme = inflationAdjustedTarget;
    yearData.gesamtsaldo = verbrauchstopfSaldo + zinstopfSaldo + investitionstopfSaldo;

    // Interest amounts calculated and applied at end of year (correct order)

    // Store transaction amounts for TransactionService
    yearData.verbrauchstopfRefillAmount = verbrauchstopfRefillAmount;
    yearData.zinstopfCashFlow = zinstopfCashFlow;
    // Internal transfer from Investitionstopf to Verbrauchstopf (refill, NOT a user
    // withdrawal). Convention matches zinstopfCashFlow: negative = outflow. The separate
    // Investitionstopf->Zinstopf refill is captured as the positive part of zinstopfCashFlow.
    yearData.investitionstopfCashFlow = -investitionstopfContributionToVerbrauchstopf;
    yearData.verbrauchstopfInterest = verbrauchstopfInterestEarned;
    yearData.zinstopfInterest = zinstopfInterestEarned;
    yearData.investitionstopfInterest = investitionstopfInterestEarned;

    // Update additional properties expected by tests
    yearData.verbrauchstopf = verbrauchstopfSaldo; // Aliases for pot balances
    yearData.zinstopf = zinstopfSaldo;
    yearData.investitionstopf = investitionstopfSaldo;
    yearData.entnahme = gesamtentnahme; // Alias for gesamtentnahme
    yearData.steuern = steuern;
    yearData.nettoentnahme = nettoentnahme;
    yearData.gesamtkapital = verbrauchstopfSaldo + zinstopfSaldo + investitionstopfSaldo; // Alias for gesamtsaldo
    yearData.rendite = yearData.investitionstopfRendite; // Already set in createYearData
    yearData.steueraufRendite = steueraufRendite;
    yearData.steueraufZinsen = steueraufZinsen;

    // Pot-specific withdrawals (FIXED - per-pot DIRECT user withdrawals)
    // BUG FIX: entnahmeVerbrauchstopf is no longer overwritten with gesamtentnahme.
    // It keeps the actual withdrawal from the Verbrauchstopf balance (set above), while
    // direct withdrawals from Zinstopf/Investitionstopf (remainingNeed phase in depletion
    // years) are recorded in their own fields. Invariant:
    // entnahmeVerbrauchstopf + entnahmeZinstopf + entnahmeInvestitionstopf === gesamtentnahme
    yearData.entnahmeVerbrauchstopf = actualVerbrauchstopfWithdrawal;
    yearData.entnahmeZinstopf = direktentnahmeZinstopf;
    yearData.entnahmeInvestitionstopf = direktentnahmeInvestitionstopf;
    yearData.nettoentnahmeVerbrauchstopf = actualVerbrauchstopfWithdrawal; // No tax calculations

    // Build structured info: Verbrauchstopf first, then Zinstopf-Ziel, then Zinstopf infos
    const zinstopfTargetLine = `Zinstopf-Ziel: ${Math.round(zinstopfZielgroesse).toLocaleString('de-DE')} €`;
    
    const infoLines = [];
    
    if (isFirstYear) {
      // First year: No refills, only initial setup and withdrawals
      infoLines.push('• Startjahr: Nur Entnahme, keine Auffüllungen');
      infoLines.push(`• ${zinstopfTargetLine}`);
    } else if (
      gesamtentnahme === 0 &&
      verbrauchstopfSaldo + zinstopfSaldo + investitionstopfSaldo === 0
    ) {
      infoLines.push('• Portfolio vollständig erschöpft');
      infoLines.push(`• ${zinstopfTargetLine}`);
    } else {
      // Verbrauchstopf actions first
      if (strategyDescription) {
        infoLines.push(`• Verbrauchstopf: ${strategyDescription}`);
      } else if (yearData.istKrise) {
        infoLines.push('• Verbrauchstopf: Entnahme aus verfügbaren Töpfen');
      } else {
        infoLines.push('• Verbrauchstopf: Keine Auffüllung erforderlich');
      }
      
      // Then Zinstopf-Ziel
      infoLines.push(`• ${zinstopfTargetLine}`);
      
      // Then Zinstopf actions
      if (topf2AuffuellAktion && !topf2AuffuellAktion.includes('Keine Auffüllung') && !topf2AuffuellAktion.includes('Startjahr')) {
        infoLines.push(`• Zinstopf: ${topf2AuffuellAktion}`);
      } else {
        infoLines.push('• Zinstopf: Keine Auffüllung');
      }
    }
    
    // Combine all lines
    yearData.auffuellAktion = infoLines.join('\n');
    yearData.topf1AuffuellQuelle = isFirstYear ? 'Startjahr: Keine Auffüllung' : (topf1AuffuellQuelle || 'Keine Auffüllung');
    yearData.topf2AuffuellAktion = topf2AuffuellAktion || 'Keine Auffüllung';
    
    // Store overflow transfer amount for transaction visibility
    yearData.zinstopfUeberschussTransfer = zinstopfUeberschussTransfer;
  }
}

// Legacy exports for backward compatibility
export { ThreeTopfSimulationEngine as SimulationEngine };
export type { ThreeTopfYearData as YearData };
export type { ThreeTopfSimulationOptions as SimulationOptions };
export type { ThreeTopfInitialValues as InitialValues };
